import { useQuestSchedule, type QSDay, type QSSlot } from "../../hooks/useQuestSchedule";
import css from "./QuestScheduleScreen.module.css";

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const DOW_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

/** "2026-06-04" → "4 июн". */
function fmtDay(date: string): string {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return date;
  const day = parseInt(m[3]!, 10);
  const mon = MONTHS_SHORT[parseInt(m[2]!, 10) - 1] || m[2];
  return `${day} ${mon}`;
}

export interface QuestScheduleScreenProps {
  questId: string;
  /** Имя квеста для шапки (из сетки), пока грузится расписание. */
  questName?: string;
  onBack: () => void;
  /** Тап по дню/слоту — перейти в обычную сетку на эту дату. */
  onPickDate: (dateStr: string) => void;
}

/**
 * DC: расписание одного квеста на много дней вперёд (порт V1-фичи).
 * Тап по названию квеста в сетке открывает этот экран; здесь — лента дней
 * с прокруткой, в каждом дне слоты с ценой и статусом. Тап по слоту → переход
 * в обычную сетку на выбранную дату. Кнопка «Назад» возвращает в сетку.
 */
export function QuestScheduleScreen({ questId, questName, onBack, onPickDate }: QuestScheduleScreenProps) {
  const { quest, days, loading, error } = useQuestSchedule(questId, 30);
  const title = quest?.name || questName || "Расписание";

  return (
    <div className={css.screen}>
      <div className={css.bar}>
        <button type="button" className={css.back} onClick={onBack} aria-label="Назад в сетку">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 5 L8 12 L15 19" />
          </svg>
          <span>Назад</span>
        </button>
        <div className={css.title} title={title}>{title}</div>
      </div>

      {error ? (
        <div className={css.msg}>Ошибка: {error}</div>
      ) : loading && !days.length ? (
        <div className={css.msg}>Загрузка расписания…</div>
      ) : !days.length ? (
        <div className={css.msg}>Нет данных по расписанию</div>
      ) : (
        <div className={css.scroll}>
          {days.map((d) => (
            <DayBlock key={d.date} day={d} onPick={() => onPickDate(d.date)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DayBlock({ day, onPick }: { day: QSDay; onPick: () => void }) {
  const weekend = day.dow === 0 || day.dow === 6;
  return (
    <section className={css.day}>
      <header className={`${css.dayHead} ${weekend ? css.weekend : ""}`}>
        <span className={css.dayDate}>{fmtDay(day.date)}</span>
        <span className={css.dayDow}>{DOW_SHORT[day.dow] || ""}</span>
      </header>
      <div className={css.slots}>
        {day.slots.map((s) => (
          <SlotCell key={s.time} slot={s} onPick={onPick} />
        ))}
      </div>
    </section>
  );
}

function SlotCell({ slot, onPick }: { slot: QSSlot; onPick: () => void }) {
  const st =
    slot.status === "paid" ? css.st_paid :
    slot.status === "booked" ? css.st_booked :
    slot.status === "past" ? css.st_past :
    css.st_free;
  return (
    <button type="button" className={`${css.slot} ${st}`} onClick={onPick} title={slot.client || undefined}>
      <span className={css.slotTime}>{slot.time}</span>
      {slot.price > 0 ? <span className={css.slotPrice}>{slot.price}</span> : null}
    </button>
  );
}
